import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Send as SendIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import emailSettingsApi from '@crema/services/apis/emailSettings';

const SmtpConfigurationForm = ({settings, onSuccess, onError}) => {
  const [formData, setFormData] = useState({
    email_host: '',
    email_port: 587,
    email_username: '',
    email_password: '',
    from_email: '',
    from_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (settings) {
      setFormData({
        email_host: settings.email_host || '',
        email_port: settings.email_port || 587,
        email_username: settings.email_username || '',
        email_password: settings.email_password || '', // Backend returns '***masked***' for existing passwords
        from_email: settings.from_email || '',
        from_name: settings.from_name || '',
      });
    }
  }, [settings]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleInputChange = (field) => (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;

    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      console.log('🔍 Form data updated:', {field, value, newData});
      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };


  const validateForm = () => {
    const newErrors = {};

    if (!formData.email_host.trim()) {
      newErrors.email_host = 'SMTP Host is required';
    }

    if (
      !formData.email_port ||
      formData.email_port < 1 ||
      formData.email_port > 65535
    ) {
      newErrors.email_port = 'Valid port number is required (1-65535)';
    }

    if (!formData.email_username.trim()) {
      newErrors.email_username = 'Username is required';
    }

    // Only require password if it's a new configuration (not masked password from backend)
    if (!formData.email_password.trim()) {
      newErrors.email_password = 'Password is required';
    } else if (formData.email_password === '***masked***' && !settings?.email_password) {
      // Masked password is only valid if there's an existing setting
      newErrors.email_password = 'Password is required';
    }

    if (!formData.from_email.trim()) {
      newErrors.from_email = 'From Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.from_email)) {
      newErrors.from_email = 'Valid email address is required';
    }

    if (!formData.from_name.trim()) {
      newErrors.from_name = 'From Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      onError('Please fix form errors before testing');
      return;
    }

    try {
      if (isMountedRef.current) {
        setTesting(true);
      }
      
      // Backend will handle masked password - just send the form data as is
      console.log('🔍 Frontend sending SMTP test data:', formData);
      console.log(
        '🔍 smtp_secure value:',
        formData.smtp_secure,
        'type:',
        typeof formData.smtp_secure,
      );
      console.log('🔍 Switch checked state:', !formData.smtp_secure);
      const response = await emailSettingsApi.testSmtpConnection(formData);

      if (isMountedRef.current) {
        if (response.data.status === 200 && response.data.data.isValid) {
          onSuccess('SMTP connection test successful');
        } else {
          onError('SMTP connection test failed');
        }
      }
    } catch (error) {
      console.error('Error testing SMTP connection:', error);
      if (isMountedRef.current) {
        onError('Failed to test SMTP connection');
      }
    } finally {
      if (isMountedRef.current) {
        setTesting(false);
      }
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      onError('Please fix form errors before saving');
      return;
    }

    try {
      if (isMountedRef.current) {
        setSaving(true);
      }
      
      // Backend will handle masked password - just send the form data as is
      const response = await emailSettingsApi.updateEmailSettings(formData);

      if (isMountedRef.current) {
        if (response.data.status === 200) {
          onSuccess('SMTP configuration saved successfully');
        } else {
          onError(response.data.message || 'Failed to save SMTP configuration');
        }
      }
    } catch (error) {
      console.error('Error saving SMTP configuration:', error);
      if (isMountedRef.current) {
        onError('Failed to save SMTP configuration');
      }
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        SMTP Configuration
      </Typography>

      <Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
        Configure your SMTP server settings for sending daily reports
      </Typography>

      <Card>
        <CardContent>
          <Box component='form' noValidate>
            <Box display='flex' gap={2} mb={3}>
              <TextField
                fullWidth
                label='SMTP Host'
                value={formData.email_host}
                onChange={handleInputChange('email_host')}
                error={!!errors.email_host}
                helperText={errors.email_host || 'e.g., smtp.gmail.com'}
                required
              />

              <TextField
                label='Port'
                type='number'
                value={formData.email_port}
                onChange={handleInputChange('email_port')}
                error={!!errors.email_port}
                helperText={errors.email_port || '587 for TLS, 465 for SSL'}
                required
                sx={{minWidth: 120}}
              />
            </Box>

            <Box display='flex' gap={2} mb={3}>
              <TextField
                fullWidth
                label='Username'
                value={formData.email_username}
                onChange={handleInputChange('email_username')}
                error={!!errors.email_username}
                helperText={errors.email_username || 'Your email address'}
                required
              />

              <TextField
                fullWidth
                label='Password'
                type={showPassword ? 'text' : 'password'}
                value={formData.email_password}
                onChange={handleInputChange('email_password')}
                error={!!errors.email_password}
                helperText={
                  errors.email_password || 'Your email password or app password'
                }
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge='end'
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box display='flex' gap={2} mb={3}>
              <TextField
                fullWidth
                label='From Email'
                value={formData.from_email}
                onChange={handleInputChange('from_email')}
                error={!!errors.from_email}
                helperText={errors.from_email || 'Sender email address'}
                required
              />

              <TextField
                fullWidth
                label='From Name'
                value={formData.from_name}
                onChange={handleInputChange('from_name')}
                error={!!errors.from_name}
                helperText={errors.from_name || 'Sender display name'}
                required
              />
            </Box>

            <Box display='flex' gap={2} justifyContent='flex-end'>
              <Button
                variant='outlined'
                startIcon={<SendIcon />}
                onClick={handleTestConnection}
                disabled={testing || saving}
              >
                {testing ? (
                  <>
                    <CircularProgress size={16} sx={{mr: 1}} />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>

              <Button
                variant='contained'
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={testing || saving}
              >
                {saving ? (
                  <>
                    <CircularProgress size={16} sx={{mr: 1}} />
                    Saving...
                  </>
                ) : (
                  'Save Configuration'
                )}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Alert severity='info' sx={{mt: 2}}>
        <Typography variant='body2'>
          <strong>Common SMTP Settings:</strong>
          <br />
          • Gmail: smtp.gmail.com, Port 587, TLS enabled
          <br />
          • Outlook: smtp-mail.outlook.com, Port 587, TLS enabled
          <br />• Yahoo: smtp.mail.yahoo.com, Port 587, TLS enabled
        </Typography>
      </Alert>
    </Box>
  );
};

SmtpConfigurationForm.propTypes = {
  settings: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default SmtpConfigurationForm;
